import {
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  Spinner,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react'
import _ from 'lodash'
import moment from 'moment'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlineEdit } from 'react-icons/ai'
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa'
import { NavLink } from 'react-router-dom'
import type { CellProps, Column } from 'react-table'
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { BaseLayout } from '..'
import AddNewAgencyAccountModal from '../../components/Agency/AddNewAgencyAccountModal'
import Pagination from '../../components/Pagination'
import SearchBar from '../../components/SearchBar'
import type AgencyChildAccountsResponse from '../../interfaces/AgencyChildAccountsResponse'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import useToast from '../../hooks/useToast'

const ManageAccountsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()

  const { user } = useContext(UserContext)

  const [data, setData] = useState<AgencyChildAccountsResponse[]>()

  const { request, loading } = useFetch<AgencyChildAccountsResponse[]>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/agency/child/`,
      )

      if (request.error) {
        toast({
          description: t('error_fetching_data', 'Error in fetching data. Try refreshing the page.'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else {
        setData(response)
      }
    }
  }

  useEffect(() => {
    void getData()
  }, [user])

  const tableData = useMemo<AgencyChildAccountsResponse[]>(
    () => data ?? [],
    [data],
  )

  const columns = useMemo<Array<Column<AgencyChildAccountsResponse>>>(
    () => [
      {
        Header: t('client_name', 'Client Name').toString(),
        accessor: ({ clientName }) => clientName,
      },
      {
        Header: t('imagekit_id', 'ImageKit ID').toString(),
        accessor: ({ imagekitId }) => imagekitId,
      },
      {
        Header: t('processing_region', 'Processing Region').toString(),
        accessor: ({ region }) => region,
      },
      {
        Header: t('bill_to', 'Bill To').toString(),
        accessor: ({ billTo }) => _.capitalize(billTo),
      },
      {
        Header: t('created_on', 'Created On').toString(),
        accessor: ({ createdAt }) => new Date(createdAt),
        sortType: 'datetime',
        Cell: ({ value }: CellProps<AgencyChildAccountsResponse, Date>) =>
          moment(value).format('MMMM Do YYYY, h:mm:ss a'),
      },
      {
        Header: t('actions', 'Actions').toString(),
        accessor: ({ clientNumber }) => clientNumber,
        Cell: ({ value }: CellProps<AgencyChildAccountsResponse, string>) => (
          <Button
            as={NavLink}
            variant="link"
            colorScheme="appSlate"
            leftIcon={<Icon as={AiOutlineEdit} color="appBlue" />}
            to={`/manage-accounts/${value}`}
          >
            Edit
          </Button>
        ),
      },
    ],
    [],
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    state: { pageIndex, pageSize, ...tableState },
    canPreviousPage,
    canNextPage,
    pageCount,
    pageOptions,
    setGlobalFilter,
    setPageSize,
    previousPage,
    nextPage,
    gotoPage,
  } = useTable<AgencyChildAccountsResponse>(
    { columns, data: tableData },
    useGlobalFilter,
    useSortBy,
    usePagination,
  )

  const pageOffset = pageIndex * pageSize

  const { isOpen, onClose, onOpen } = useDisclosure({
    onClose: () => {
      void getData()
    },
  })

  return (
    <BaseLayout>
      <MainContentHeadingBox>
        <Heading
          as="h5"
          size="md"
          fontWeight="600"
          color={colors.appDarkIndigo}
          alignSelf="center"
        >
          {t('account_management', 'Account Management')}
        </Heading>
      </MainContentHeadingBox>
      <MainContentWrapper style={{ gap: '0.5em' }}>
        <Flex justifyContent="space-between" marginY="3">
          <Button onClick={onOpen}>Add Account</Button>
          <HStack>
            {rows.length ? (
              <ChakraText fontWeight="500">
                Showing {pageOffset + 1} to{' '}
                {pageOffset + pageSize <= rows.length
                  ? pageOffset + pageSize
                  : rows.length}{' '}
                of {rows.length} entries
              </ChakraText>
            ) : (
              <ChakraText fontWeight="500">No entries available</ChakraText>
            )}
            <SearchBar
              searchQuery={tableState.globalFilter as string}
              setSearchQuery={setGlobalFilter}
              minWidth="30em"
            />
          </HStack>
        </Flex>
        <Table {...getTableProps}>
          <Thead bgColor="appPaleGreyTwo">
            {headerGroups.map(headerGroup => {
              const { key, ...restHeaderGroupProps } =
                headerGroup.getHeaderGroupProps()
              return (
                <Tr key={key} {...restHeaderGroupProps}>
                  {headerGroup.headers.map(column => {
                    const { key: keyCol, ...restColumn } =
                      column.getHeaderProps(column.getSortByToggleProps())
                    return (
                      <Th key={keyCol} {...restColumn}>
                        {column.render('Header')}
                        {!column.disableSortBy ? (
                          <Box
                            as="span"
                            pl="2"
                            color={colors.appBattleshipGrey}
                          >
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <Icon
                                  as={FaSortUp}
                                  aria-label={t(
                                    'sorted_descending',
                                    'sorted descending',
                                  )}
                                />
                              ) : (
                                <Icon
                                  as={FaSortDown}
                                  aria-label={t(
                                    'sorted_ascending',
                                    'sorted ascending',
                                  )}
                                />
                              )
                            ) : (
                              <Icon
                                as={FaSort}
                                aria-label={t(
                                  'sort_by_column',
                                  'sort by column',
                                )}
                              />
                            )}
                          </Box>
                        ) : null}
                      </Th>
                    )
                  })}
                </Tr>
              )
            })}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {!loading ? (
              rows.length ? (
                page.map(row => {
                  prepareRow(row)
                  const { key, ...restRowProps } = row.getRowProps()
                  return (
                    <Tr key={key} {...restRowProps}>
                      {row.cells.map(cell => {
                        const { key: keyRow, ...restCellProps } =
                          cell.getCellProps()
                        return (
                          <Td key={keyRow} {...restCellProps}>
                            <ChakraText>{cell.render('Cell')}</ChakraText>
                          </Td>
                        )
                      })}
                    </Tr>
                  )
                })
              ) : (
                <Tr>
                  <Td colSpan={6} textAlign="center">
                    <ChakraText>No accounts available</ChakraText>
                  </Td>
                </Tr>
              )
            ) : (
              <Tr>
                <Td colSpan={6} textAlign="center">
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="appPaleGrey"
                    color="appBlue"
                    size="xl"
                  />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
        {pageOptions.length > 0 ? <Divider mt={4} mb={4} /> : null}
        <Flex justifyContent="flex-end" alignItems="center">
          <Pagination
            pageIndex={pageIndex}
            pageSize={pageSize}
            canPreviousPage={canPreviousPage}
            canNextPage={canNextPage}
            pageCount={pageCount}
            pageOptions={pageOptions}
            setPageSize={setPageSize}
            previousPage={previousPage}
            nextPage={nextPage}
            gotoPage={gotoPage}
          />
        </Flex>
      </MainContentWrapper>
      <AddNewAgencyAccountModal isOpen={isOpen} onClose={onClose} />
    </BaseLayout>
  )
}

export default ManageAccountsLayout
